import { refreshTokens } from './auth-api';
import { clearTokens, getTokens, setTokens, type AuthTokens } from './auth-storage';

export const sessionEndedMessage = 'Your session has ended. Sign in again to continue.';
let ended = false;
let flight: { source: AuthTokens; promise: Promise<AuthTokens> } | null = null;
let identity = '';
const renewedTokens = new Set<string>();

/** Only a verified refresh keeps the same UI identity; a new login remounts it. */
export function sessionIdentity(token: string): string {
  return renewedTokens.has(token) ? identity : token;
}
export function sessionHasEnded(): boolean {
  return !getTokens() && ended;
}
function endSession(source: AuthTokens): void {
  if (getTokens() !== source) return;
  ended = true;
  clearTokens();
}
export function renewSession(): Promise<AuthTokens> {
  const source = getTokens();
  if (!source) return Promise.reject(new Error(sessionEndedMessage));
  if (flight?.source === source) return flight.promise;
  const promise = (async () => {
    try {
      if (!source.refreshToken) throw new Error(sessionEndedMessage);
      const result = await refreshTokens(source.refreshToken);
      // A completed refresh must never undo a logout or replace another login.
      if (getTokens() !== source) throw new Error(sessionEndedMessage);
      const next: AuthTokens = {
        accessToken: result.accessToken,
        idToken: result.idToken,
        refreshToken: result.refreshToken ?? source.refreshToken,
        expiresAt: Date.now() + result.expiresIn * 1000,
      };
      if (!renewedTokens.has(source.accessToken)) {
        identity = source.accessToken;
        renewedTokens.clear();
      }
      renewedTokens.add(source.accessToken);
      renewedTokens.add(next.accessToken);
      ended = false;
      setTokens(next);
      return next;
    } catch {
      endSession(source);
      throw new Error(sessionEndedMessage);
    } finally {
      if (flight?.source === source) flight = null;
    }
  })();
  flight = { source, promise };
  return promise;
}
function withToken(init: RequestInit, token: string): RequestInit {
  const headers = new Headers(init.headers);
  headers.set('Authorization', `Bearer ${token}`);
  return { ...init, headers: Object.fromEntries(headers) };
}
function unauthorized(): Response {
  return new Response(JSON.stringify({ error: 'UNAUTHORIZED', message: sessionEndedMessage }), { status: 401 });
}
/** Reusable JSON, FormData and download requests; auth endpoints use raw fetch. */
export async function authenticatedFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const supplied = new Headers(init.headers).get('Authorization')?.replace(/^Bearer /, '');
  const source = getTokens();
  // Explicit-token clients remain usable without a stored browser session.
  if (!source || !supplied) return fetch(url, init);
  const belongs = supplied === source.accessToken ||
    (renewedTokens.has(supplied) && renewedTokens.has(source.accessToken));
  if (!belongs) return unauthorized();
  if (init.signal?.aborted) throw init.signal.reason;
  let token = source.accessToken;
  let refreshed = false;
  if (Date.now() >= source.expiresAt) {
    try { token = (await renewSession()).accessToken; refreshed = true; }
    catch { return unauthorized(); }
  }
  const response = await fetch(url, token === supplied ? init : withToken(init, token));
  if (response.status !== 401 || init.signal?.aborted) return response;
  const current = getTokens();
  if (!current || (current.accessToken !== token && !(renewedTokens.has(token) && renewedTokens.has(current.accessToken)))) return response;
  if (refreshed) { endSession(current); return response; }
  let next: AuthTokens;
  try {
    // Another request may already have renewed the token while this one was in flight.
    next = current.accessToken !== token ? current : await renewSession();
  } catch { return unauthorized(); }
  if (init.signal?.aborted) throw init.signal.reason;
  const retry = await fetch(url, withToken(init, next.accessToken));
  if (retry.status === 401) endSession(next);
  return retry;
}

/** Reschedule after rotation and wake from a suspended tab using the known expiry. */
export function startSessionRenewal(): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;
  let stopped = false;
  const schedule = () => {
    clearTimeout(timer);
    const tokens = getTokens();
    if (stopped || !tokens) return;
    const remaining = tokens.expiresAt - Date.now();
    const delay = remaining <= 0 ? 0 : !tokens.refreshToken ? remaining : remaining > 60000 ? remaining - 60000 : Math.max(1000, remaining * 0.8);
    timer = setTimeout(() => {
      if (!tokens.refreshToken) endSession(tokens);
      else void renewSession().catch(() => { /* endSession notifies the guard */ });
    }, Math.min(delay, 2147483647));
  };
  window.addEventListener('nissei-session-change', schedule);
  window.addEventListener('focus', schedule);
  schedule();
  return () => {
    stopped = true;
    clearTimeout(timer);
    window.removeEventListener('nissei-session-change', schedule);
    window.removeEventListener('focus', schedule);
  };
}
